import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { useConfirm } from './confirm';
import { useAuth } from '../contexts/AuthContext';
import { useDsrByEmail } from '../hooks/usePrivacyQueries';
import { useCancelDataSubjectRequest } from '../hooks/usePrivacyMutations';
import { CANCELABLE_DSR_STATUS } from './constant';
import { PageHeader, Card, StatusBadge, getStatusVariant, EmptyState, SkeletonLoader, MessageBar, DataTable } from './ui';

interface DataSubjectRequestItem {
  id: string;
  requestType: string;
  requestDetails?: string;
  status: string;
  createdDate: string;
  downloadUrl?: string;
}

export const DataSubjectRequestList: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { confirm } = useConfirm();
  const [dataSubjectRequest, setDataSubjectRequest] = useState<DataSubjectRequestItem[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const { data: dsrData, isLoading, refetch } = useDsrByEmail(user?.email || '', isAuthenticated);
  const cancelRequestMutation = useCancelDataSubjectRequest();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (dsrData?.dataSubjects) {
      setDataSubjectRequest(dsrData.dataSubjects);
    }
  }, [dsrData]);

  const downloadData = (row: DataSubjectRequestItem) => {
    if (row.downloadUrl) {
      window.location.href = row.downloadUrl;
    }
  };

  const cancelRequest = async (row: DataSubjectRequestItem) => {
    if (isRunning) {
      setErrorMessage('Cancel request already processing.');
      setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    try {
      const confirmed = await confirm('Are you sure want to cancel this request?', {
        title: 'Cancel ?',
        okLabel: 'Ok',
        cancelLabel: 'Cancel',
      });

      if (!confirmed) return;

      setIsRunning(true);
      cancelRequestMutation.mutate(
        { id: row.id, email: user?.email || '' },
        {
          onSuccess: (data) => {
            setIsRunning(false);
            if (!data.isError) {
              setSuccessMessage(data.message || 'Request cancelled successfully');
              setTimeout(() => setSuccessMessage(null), 10000);
              refetch();
            } else {
              setErrorMessage(data.responseException?.exceptionMessage || 'Failed to cancel request');
              setTimeout(() => setErrorMessage(null), 10000);
            }
          },
          onError: () => {
            setIsRunning(false);
            setErrorMessage('Failed to cancel request');
            setTimeout(() => setErrorMessage(null), 10000);
          },
        }
      );
    } catch {
      // User cancelled the confirmation dialog
    }
  };

  const columns: ColumnDef<DataSubjectRequestItem, any>[] = [
    {
      accessorKey: 'requestType',
      header: 'Request Type',
    },
    {
      accessorKey: 'requestDetails',
      header: 'Request Details',
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge label={row.original.status} variant={getStatusVariant(row.original.status)} />
      ),
    },
    {
      id: 'date',
      header: 'Created Date',
      cell: ({ row }) => new Date(row.original.createdDate).toLocaleString(),
    },
    {
      id: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const item = row.original;
        if (CANCELABLE_DSR_STATUS.includes(item.status)) {
          return (
            <button
              onClick={() => cancelRequest(item)}
              className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
            >
              Cancel
            </button>
          );
        } else if (item.downloadUrl && item.downloadUrl !== '') {
          return (
            <button
              onClick={() => downloadData(item)}
              className="bg-primary text-white hover:bg-primary/90 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors"
            >
              Download Data
            </button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <PageHeader title="Data Subject Requests" />

      {/* Messages */}
      {successMessage && (
        <div className="mb-4">
          <MessageBar variant="success">{successMessage}</MessageBar>
        </div>
      )}
      {errorMessage && (
        <div className="mb-4">
          <MessageBar variant="error">{errorMessage}</MessageBar>
        </div>
      )}

      <Card className="p-4 sm:p-6 overflow-x-auto">
        {isLoading ? (
          <SkeletonLoader variant="table-row" count={5} />
        ) : dataSubjectRequest.length === 0 ? (
          <EmptyState
            title="No requests yet"
            description="You haven't submitted any data subject requests."
            icon={
              <svg className="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 3h1a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
              </svg>
            }
          />
        ) : (
          <DataTable
            data={dataSubjectRequest}
            columns={columns}
            grouping={['requestType']}
          />
        )}
      </Card>
    </div>
  );
};
